import { LoadingScreen } from '@avanta/shared/components/LoadingScreen';
import { Button } from '@avanta/shared/components/ui/Button';
import { Separator } from '@avanta/shared/components/ui/Separator';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { UserType } from '@avanta/shared/constants/users';
import { useDialogContext } from '@avanta/shared/contexts/Dialog';
import { useFetchPatient } from '@avanta/shared/hooks/useFetchPatient';
import {
  useActiveConversation,
  useConversationsStore,
} from '@avanta/shared/stores/chat/conversations';
import { genderOptions } from '@avanta/shared/types/patients';
import { Building, type LucideIcon, Mail, Phone, User, X } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { InfoRow } from 'components/InfoRow';
import { Text } from 'components/Text';
import { UserProfileSummary } from 'components/UserProfileSummary';
import { useIsPatientOrEmployee, useUserStore } from 'stores/user';
import { CHAT_BUTTON_CLASSNAME } from '..';
import { CreateCouponForm } from './components/CreateCouponForm';
import { RemainingSessions } from './components/RemainingSessions';

export const UserInfo = (): React.ReactElement | null => {
  const user = useUserStore(state => state.user);
  const activeConversation = useActiveConversation();
  const setIsOpenInfo = useConversationsStore(state => state.setIsOpenInfo);
  const { data: patient, isLoading } = useFetchPatient(activeConversation?.otherUser.id ?? '');
  const { showDialog } = useDialogContext();
  const isPatientOrEmployee = useIsPatientOrEmployee();
  const isSupporter = user?.type === UserType.SUPPORTER;

  const { t } = useTranslation(LOCIZE_NS.CHAT);

  const handleOpenCreateCoupon = (): void => {
    showDialog({ component: <CreateCouponForm /> });
  };

  if (!activeConversation || isPatientOrEmployee) {
    return null;
  }

  const genderOpt = genderOptions.find(opt => opt.value === patient?.gender);
  const genderText = genderOpt ? t(genderOpt.label, { ns: genderOpt.ns }) : '';

  const info: { data: string; icon: LucideIcon }[] = [
    {
      data: `${patient?.age} (${genderText})`,
      icon: User,
    },
    ...(patient?.email ? [{ data: patient?.email, icon: Mail }] : []),
    ...(patient?.contactPhone ? [{ data: patient?.contactPhone, icon: Phone }] : []),
    ...(patient?.enterpriseId ? [{ data: patient?.enterpriseId, icon: Building }] : []),
  ];

  return (
    <div className="flex flex-col w-full flex-1 am-xl:w-[320px] am-xl:flex-none bg-white overflow-y-auto border-t border-r border-b border-plum-200 rounded-r-lg">
      <div className="flex justify-between items-center p-4 shrink-0">
        <h2 className="text-md font-medium">{t('Chat.UserInfo.heading')}</h2>
        <Button
          variant="ghost"
          icon="only"
          onClick={() => setIsOpenInfo(false)}
          className={CHAT_BUTTON_CLASSNAME}
        >
          <X />
        </Button>
      </div>
      {isLoading ? (
        <LoadingScreen />
      ) : !patient ? (
        <div className="flex flex-col gap-4 p-4">
          <Text variant="text-sm-medium" className="text-denim-600">
            {t('Chat.UserInfo.error.title')}
          </Text>
          <Trans i18nKey="Chat.UserInfo.error">
            <Text variant="text-sm-regular" className="text-denim-600">
              {t('Chat.UserInfo.error.description')}
            </Text>
          </Trans>
        </div>
      ) : (
        <div className="flex flex-col gap-8 py-8 px-4">
          <UserProfileSummary
            firstName={patient.firstName}
            surname={patient.surname}
            nickname={patient.nickname}
            userId={activeConversation.otherUser.id}
          />
          <Separator />
          <div className="flex flex-col gap-4">
            <h3 className="text-lg font-medium">{t('Chat.UserInfo.personalInfo.heading')}</h3>
            {info.map(({ data, icon }) => (
              <InfoRow key={data} data={data} icon={icon} />
            ))}
          </div>
          {isSupporter && (
            <>
              <Separator />
              <div className="flex flex-col gap-4">
                <RemainingSessions patient={patient} />
                <Button onClick={handleOpenCreateCoupon} variant="terciary">
                  {t('Chat.UserInfo.Coupons.cta')}
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
